import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronRight } from 'lucide-react';
import { getSettings } from '../lib/api';
import { SocialSidebar, CallButton } from '../components/FloatingElements';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../lib/LanguageContext';

const LOGO_URL = "/logo.png";

export default function PublicLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [settings, setSettings] = useState({});
  const location = useLocation();
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/leistungen', label: t('nav.services') },
    { path: '/faq', label: t('nav.faq') },
    { path: '/kontakt', label: t('nav.contact') },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-[#0A0A0A] text-white py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${settings.phone || '0176 34681594'}`} className="flex items-center gap-2 hover:text-[#D4B982] transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">{settings.phone || '0176 34681594'}</span>
            </a>
            <a href={`mailto:${settings.email || 'info-adservice@gmx.de'}`} className="flex items-center gap-2 hover:text-[#D4B982] transition-colors">
              <Mail size={14} />
              <span className="hidden sm:inline">{settings.email || 'info-adservice@gmx.de'}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#D4B982] italic text-xs sm:text-sm hidden sm:block">{settings.slogan || 'Zuverlässig, schnell und sauber.'}</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'navbar-scrolled py-3' : 'bg-white py-4'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="A&D Service" style={{ width: '100px', height: '100px' }} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium tracking-wide uppercase transition-colors hover:text-[#D4B982] ${
                  location.pathname === link.path ? 'text-[#D4B982]' : 'text-[#0A0A0A]'
                }`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#D4B982]" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/kontakt"
            className="hidden md:flex items-center gap-2 bg-[#D4B982] text-white px-6 py-3 text-sm font-medium uppercase tracking-wider hover:bg-[#C5A059] transition-colors btn-ripple"
            data-testid="nav-cta-button"
          >
            {t('nav.requestQuote')}
            <ChevronRight size={16} />
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-[#0A0A0A]"
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-[#E4E4E7] shadow-lg animate-fade-in">
            <div className="flex flex-col py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-6 py-3 text-sm font-medium uppercase tracking-wide ${
                    location.pathname === link.path ? 'text-[#D4B982] bg-[#F4F4F5]' : 'text-[#0A0A0A]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-6 pt-4">
                <Link
                  to="/kontakt"
                  className="flex items-center justify-center gap-2 bg-[#D4B982] text-white px-6 py-3 text-sm font-medium uppercase tracking-wider"
                >
                  {t('nav.requestQuote')}
                  <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Floating Elements */}
      <SocialSidebar />
      <CallButton phone={settings.phone || '0176 34681594'} />

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Logo & Info */}
            <div className="md:col-span-2">
            <img src={LOGO_URL} alt="A&D Service" style={{ width: '100px', height: '100px' }} />
              <p className="text-[#71717A] my-4 max-w-md">
                {t('footer.description')}
              </p>
              <p className="text-[#D4B982] italic">{settings.slogan || 'Zuverlässig, schnell und sauber.'}</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#D4B982]">{t('footer.navigation')}</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-[#71717A] hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#D4B982]">{t('footer.contact')}</h4>
              <ul className="space-y-3">
                <li>
                  <a href={`tel:${settings.phone || '0176 34681594'}`} className="flex items-center gap-2 text-[#71717A] hover:text-white transition-colors">
                    <Phone size={16} />
                    {settings.phone || '0176 34681594'}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${settings.email || 'info-adservice@gmx.de'}`} className="flex items-center gap-2 text-[#71717A] hover:text-white transition-colors">
                    <Mail size={16} />
                    {settings.email || 'info-adservice@gmx.de'}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#262626] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#71717A] text-sm">
              © {new Date().getFullYear()} A&D Service. {t('footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
